import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/App";
import { Mail, MapPin, Send, Github, Facebook, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Admin ne doit pas accéder à la page contact
  if (user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitted(true);
        toast.success(t("contact.success"));
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: t("contact.email"), value: "michelbergertsitsok@gmail.com", href: "mailto:michelbergertsitsok@gmail.com" },
    { icon: MapPin, label: t("contact.location"), value: "Cameroun", href: null },
  ];

  const socialLinks = [
    { icon: Github, label: "GitHub", href: "https://github.com/Tsitsok98" },
    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/michelbergertsitsok" },
  ];

  return (
    <div className="min-h-screen pt-16 md:pt-20 pb-12 md:pb-16" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8 md:py-12 lg:py-16">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3 md:mb-4">
            {t("contact.title")}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto px-4">
            {t("contact.subtitle")}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6 md:p-8 rounded-2xl bg-card border border-border min-h-[400px]">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-4 md:mb-6">
                  <CheckCircle className="h-8 w-8 md:h-10 md:w-10 text-green-500" />
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-3 md:mb-4">
                  {t("contact.success")}
                </h2>
                <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6">
                  {t("contact.successMsg")}
                </p>
                <Button onClick={() => setSubmitted(false)} variant="outline">
                  {t("contact.send")}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm">{t("contact.name")}</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleChange} required data-testid="contact-name" className="text-sm md:text-base" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm">{t("contact.email")}</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="contact-email" className="text-sm md:text-base" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm">{t("contact.subject")}</Label>
                  <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required data-testid="contact-subject" className="text-sm md:text-base" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm">{t("contact.message")}</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={5} required data-testid="contact-message" className="text-sm md:text-base min-h-[120px] md:min-h-[150px]" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-full py-5 md:py-6 text-sm md:text-base" data-testid="contact-submit">
                  {loading ? t("common.loading") : (<><Send className="mr-2 h-4 w-4 md:h-5 md:w-5" />{t("contact.send")}</>)}
                </Button>
              </form>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="space-y-4 md:space-y-6 lg:space-y-8">
            <div className="space-y-3 md:space-y-4">
              {contactInfo.map((info) => (
                <div key={info.label} className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-card border border-border">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <info.icon className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs md:text-sm text-muted-foreground">{info.label}</p>
                    {info.href ? (
                      <a href={info.href} className="font-medium text-sm md:text-base text-foreground hover:text-primary transition-colors truncate block">{info.value}</a>
                    ) : (
                      <p className="font-medium text-sm md:text-base text-foreground">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 md:p-6 rounded-2xl bg-card border border-border">
              <h3 className="font-heading font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">{t("contact.followMe")}</h3>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {socialLinks.map((social) => (
                  <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-full bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-all text-xs md:text-sm">
                    <social.icon className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="font-medium">{social.label}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden aspect-video bg-card border border-border flex items-center justify-center">
              <div className="text-center p-6 md:p-8">
                <MapPin className="h-10 w-10 md:h-12 md:w-12 text-primary mx-auto mb-3 md:mb-4" />
                <p className="font-heading font-semibold text-foreground text-base md:text-lg">Cameroun</p>
                <p className="text-xs md:text-sm text-muted-foreground">Afrique Centrale</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
